import React, { memo, useCallback, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Download } from 'lucide-react';

import ShipmentChip from 'components/logistics/fulfillmentLifecycle/modals/tabs/ShipmentChip';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { cn } from 'shadcn/lib/utils';
import { downloadCSV } from 'shared/components/csvDownload';
import dayjs from 'utils/dayjsConfig';

/**
 * StatusRow — a single expandable row inside a BoxTypeSection.
 *
 * Displays a status label (e.g. "Labels Printed") with a count/total summary.
 * When expanded, shows a grid of ShipmentChip components and a Download Labels button.
 *
 * The "issue" variant receives special visual treatment (warning badge).
 *
 * @param {Object} props
 * @param {string} props.label - Human-readable status label
 * @param {Array} props.shipments - Array of { orderId, shipmentId, agingMinutes }
 * @param {number} props.total - Total shipments in the parent box type
 * @param {'order'|'shipment'} props.idMode - Which ID to display in chips
 * @param {'issue'|undefined} [props.variant] - Visual variant for special rows
 * @param {boolean} [props.informational] - When true, aging is displayed without warning/critical color-coding
 */
function StatusRow({ idMode, informational, label, shipments, total, variant }) {
    const [expanded, setExpanded] = useState(false);
    const count = shipments?.length ?? 0;
    const isIssues = variant === 'issue';

    const toggle = useCallback(() => {
        setExpanded((prev) => !prev);
    }, []);

    const handleDownloadLabels = useCallback(() => {
        if (!shipments || shipments.length === 0) return;

        const headerLabel = idMode === 'shipment' ? 'Shipment ID' : 'Order ID';
        const ids = shipments.map((s) => (idMode === 'shipment' ? s.shipmentId : s.orderId));
        const csvContent = [headerLabel, ...ids].join('\n');
        const timestamp = dayjs().format('_YYYY_MM_DD_HH_mm');
        const filename = `${label.replace(/\s+/g, '_')}${timestamp}.csv`;

        downloadCSV(csvContent, filename);
    }, [idMode, label, shipments]);

    return (
        <div className="border-b last:border-b-0">
            {/* Collapsed / Header row */}
            <button
                type="button"
                className={cn(
                    'flex w-full items-center justify-between px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors',
                    expanded && 'bg-muted/30',
                )}
                onClick={toggle}
                aria-expanded={expanded}
            >
                <span className="flex items-center gap-2">
                    {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                        <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                    <span className="font-medium">{label}</span>

                    {isIssues && count > 0 && (
                        <>
                            <Badge variant="destructive" className="gap-1 text-xs">
                                <AlertTriangle className="h-3 w-3" />
                                Issue
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                                {count} {count === 1 ? 'item requires' : 'items require'} attention
                            </span>
                        </>
                    )}
                </span>

                <span className="tabular-nums text-muted-foreground">
                    {count} / {total}
                </span>
            </button>

            {/* Expanded content */}
            {expanded && (
                <div className="px-3 pb-3 pt-1 space-y-3">
                    {/* Actions bar */}
                    <div className="flex items-center justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={handleDownloadLabels}
                            disabled={count === 0}
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download Labels
                        </Button>
                    </div>

                    {/* Shipment chips grid */}
                    {count > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {shipments.map((shipment) => (
                                <ShipmentChip
                                    key={shipment.shipmentId || shipment.orderId}
                                    orderId={shipment.orderId}
                                    shipmentId={shipment.shipmentId}
                                    agingMinutes={shipment.agingMinutes}
                                    idMode={idMode}
                                    informational={informational}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground">No shipments in this status.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default memo(StatusRow);
